import { useHotkeys } from "@mantine/hooks";
import { Button } from "@win-reality/react-ui";
import { useDispatch, useSelector } from "react-redux";

import {
  incrementTaggingFlowStep,
  setTaggingFlowStep,
} from "../../../../store/slices/hotkeys";
import {
  putTag,
  removeTag,
  setTaggingState,
} from "../../../../store/slices/session";
import type { RootState } from "../../../../store/store";

/**
 * ContactTypeStep - Select contact type
 *
 * Options:
 * - Groundball (G), Line Drive (L)
 * - Fly Ball (F), Popup (P)
 * - Bunt (B), Throwdown (T)
 */
export const ContactTypeStep = () => {
  const tagData = useSelector((state: RootState) => state.session.tagData);
  const dispatch = useDispatch();
  const tagKey = "contactType";

  function handleTag(value: string) {
    dispatch(setTaggingState(true));
    if (tagData[tagKey] === value) {
      dispatch(removeTag({ key: tagKey }));
    } else {
      dispatch(putTag({ key: tagKey, value }));
      dispatch(incrementTaggingFlowStep());
    }
  }

  const hotkeys = {
    GroundBall: "G",
    FlyBall: "F",
    Bunt: "B",
    LineDrive: "L",
    Popup: "P",
    Throwdown: "T",
  };

  useHotkeys([
    [hotkeys.GroundBall, () => handleTag("GroundBall")],
    [hotkeys.FlyBall, () => handleTag("FlyBall")],
    [hotkeys.Bunt, () => handleTag("Bunt")],
    [hotkeys.LineDrive, () => handleTag("LineDrive")],
    [hotkeys.Popup, () => handleTag("Popup")],
    [hotkeys.Throwdown, () => handleTag("Throwdown")],
  ]);

  return (
    <div className="flex w-full flex-col items-stretch gap-0">
      {/* Previously completed tags - clickable to jump back */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(0))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.prePitchStance ?? "---"}
        </p>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(1))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.pitchType ?? "---"}
        </p>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(2))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.pitchResult ?? "---"}
        </p>
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-10 cursor-pointer items-center justify-center"
        onClick={() => dispatch(setTaggingFlowStep(3))}
      >
        <p className="text-sm font-bold text-blue">
          {tagData.playResult ?? "---"}
        </p>
      </div>

      {/* Instructions */}
      <div className="flex h-10 items-center justify-center">
        <p className="text-sm italic text-text-light">Select Contact Type</p>
      </div>

      {/* Button Grid - 2 columns */}
      <div className="flex w-full gap-0">
        {/* Left Column */}
        <div className="flex flex-1 flex-col gap-0">
          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "GroundBall" ? "blue" : "ghost"}
              onClick={() => handleTag("GroundBall")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Groundball
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.GroundBall}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "FlyBall" ? "blue" : "ghost"}
              onClick={() => handleTag("FlyBall")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Fly Ball
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.FlyBall}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Bunt" ? "blue" : "ghost"}
              onClick={() => handleTag("Bunt")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Bunt
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Bunt}
                </kbd>
              </span>
            </Button>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-1 flex-col gap-0">
          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "LineDrive" ? "blue" : "ghost"}
              onClick={() => handleTag("LineDrive")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Line Drive
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.LineDrive}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Popup" ? "blue" : "ghost"}
              onClick={() => handleTag("Popup")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Popup
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Popup}
                </kbd>
              </span>
            </Button>
          </div>

          <div className="flex h-10 items-center justify-center gap-2 px-2">
            <Button
              variant={tagData[tagKey] === "Throwdown" ? "blue" : "ghost"}
              onClick={() => handleTag("Throwdown")}
              size="sm"
              className="flex-1 normal-case border border-gray-300"
            >
              <span className="flex items-center justify-between w-full">
                Throwdown
                <kbd className="ml-2 rounded bg-[hsla(218,100%,52%,0.1)] px-2 py-1 text-xs font-semibold">
                  {hotkeys.Throwdown}
                </kbd>
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
